import { ErrorBoundary } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";
import { Route, Routes, createBrowserRouter } from "react-router-dom";

import { customClientBName } from "~/config/customClientBConfig";
import envConstants from "~/config/envConstants";
import { useFlag } from "~/config/flags";
import { Analytics } from "~/features/analytics/Analytics";
import AutostoreCycleCounts from "~/features/autostoreCycleCounts/AutostoreCycleCounts";
import { AutostoreMain } from "~/features/autostoreMain/AutostoreMain";

import AutostorePicking from "~/features/autostorePicking/AutostorePicking";
import PickingPreviousBin from "~/features/autostorePreviousBin/AutostorePreviousBin";
import { AutostorePutawayV2 } from "~/features/autostorePutaway/AutostorePutawayV2";
import { AutostorePutawayOld } from "~/features/autostorePutawayOld/AutostorePutawayOld";
import Batch from "~/features/batch/Batch";
import { BatchCartPrep } from "~/features/batch/BatchCartPrep";
import { Batches as AdminBatches } from "~/features/batches/AdminBatches";
import { BinMaintenanceWorkstation } from "~/features/binMaintenanceWorkstation/BinMaintenanceWorkstation";
import { EscalatedBins } from "~/features/binMaintenanceWorkstation/EscalatedBins";
import { BinReconfiguration } from "~/features/binReconfiguration/BinReconfiguration";
import { StartBinReconfiguration } from "~/features/binReconfiguration/StartBinReconfiguration";
import { Carts } from "~/features/carts/Carts";
import CleaningBin from "~/features/cleaningBin/CleaningBin";
import CleaningBinView from "~/features/cleaningBin/CleaningBinView";
import { CreateBatches } from "~/features/createBatches/CreateBatches";
import CycleCount from "~/features/cycleCounts/CycleCount";
import { CycleCounts } from "~/features/cycleCounts/CycleCounts";
import CycleCountTask from "~/features/cycleCounts2023/CycleCountTask";
import CycleCountsV2 from "~/features/cycleCounts2023/CycleCountsV2";
import { DemoPage } from "~/features/demoPage/DemoPage";
import { DevToolsPage } from "~/features/devTools/DevToolsPage";
import { IconTablePage } from "~/features/devTools/IconTablePage";
import { UpdateConfigurationPage } from "~/features/devTools/components/UpdateConfigurationPage";
import { ManualOps } from "~/features/home/ManualOps";
import { AutostoreInventory } from "~/features/inventory/AutostoreInventory";
import { InventoryHolds } from "~/features/inventoryHolds/InventoryHolds";
import AuthCallback from "~/features/login/AuthCallback";
import { CustomClientBLogin } from "~/features/login/CustomClientBLogin";
import { Login } from "~/features/login/Login";
import { LogoutFlowSelector } from "~/features/login/LogoutFlowSelector";
import OrganizationLogin from "~/features/login/OrganizationLogin";
import ManageFlaggedInventory from "~/features/manageFlaggedInventory/ManageFlaggedInventory";
import { ManualInventory } from "~/features/manualInventory/ManualInventory";
import { OrderCreation } from "~/features/orderCreation/OrderCreation";
import { Orders } from "~/features/orders/Orders";
import { Orders as OrdersNew } from "~/features/orders/OrdersNew";
import { PickBatches } from "~/features/pickBatches/PickBatches";
import { BatchesSettingsPage } from "~/features/preview/batches/BatchesSettingsPage";
import { BinsSettingsPage } from "~/features/preview/bins/BinsSettingsPage";
import { GeneralSettingsPage } from "~/features/preview/general/GeneralSettingsPage";
import { GridViewPage } from "~/features/preview/logPublisherGrid/GridViewPage";
import {
  DEV_TOOLS_ICON_LIST_PATH,
  DEV_TOOLS_LOG_PUBLISHER_GRID_PATH,
  DEV_TOOLS_SERVICE_SUPPORT_EVENTS_PREVIEW_PATH,
  SETTINGS_PREVIEW_BATCHES_PATH,
  SETTINGS_PREVIEW_BINS_PATH,
  SETTINGS_PREVIEW_GENERAL_PATH,
  SETTINGS_PREVIEW_UPDATE_CONFIGURATION_PATH,
  SETTINGS_PREVIEW_WORKSTATION_PATH
} from "~/features/preview/paths";
import { EventTablePreview } from "~/features/preview/serviceSupportEvents/EventTablePreview";
import { SettingsWorkstationPage } from "~/features/preview/workstation/SettingsWorkstationPage";
import { Putaway } from "~/features/putaway/Putaway";
import { PutawayTask } from "~/features/putaway/PutawayTask";
import WorkstationDrillDownView from "~/features/serviceSupport/adminSummaries/WorkstationDrillDownView";
import {
  ServiceSupportAndonSectionRoute,
  ServiceSupportConfigurationsSectionRoute,
  ServiceSupportEventsSectionRoute,
  ServiceSupportRootRoute,
  ServiceSupportStatusSectionRoute,
  ServiceSupportTasksSectionRoute,
  ServiceSupportTestingToolsSectionRoute,
  ServiceSupportUnifyLegacyPathRoute,
  ServiceSupportUnifySectionRoute
} from "~/features/serviceSupport/serviceSupport.routes";
import {
  SettingsApiTokensSectionRoute,
  SettingsBatchSectionRoute,
  SettingsBinsSectionRoute,
  SettingsGeneralSectionRoute,
  SettingsGridSectionRoute,
  SettingsRootRoute,
  SettingsWorkstationSectionRoute
} from "~/features/settings/settings.routes";
import { Ship } from "~/features/ship/Ship";
import { ShipOrderTable } from "~/features/shipOrder/ShipOrderTable";
import { StageTotes } from "~/features/stage/StageTotes";
import useUnauthorizedRedirect from "~/hooks/useUnauthorizedRedirect";
import { isHomePageTypeIncluded } from "~/lib/helpers";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";

import { AppProviders } from "./AppProviders";

import { AutostoreLayout } from "./AutostoreLayout";
import { BasicLayout } from "./BasicLayout";
import ConditionalFeatureRouteWrapper from "./ConditionalFeatureRouteWrapper";
import { ContextReset } from "./ContextReset";
import { LoginLayout } from "./LoginLayout";
import PageNotFound from "./PageNotFound";
import { PrivateRoute } from "./PrivateRoute";
import { RedirectToAutostoreMain } from "./RedirectToAutostoreMain";
import { useAppSelector } from "./store";

export const Root = () => {
  const clientConfig = useAppSelector(selectServerConfig);
  const {
    dev_orderCreation,
    binCleaningEnabled,
    fc_autostore_homepages,
    manualOpsEnabled,
    dev_ptlSimulation,
    dev_conveyanceSimulation
  } = clientConfig;
  const isAdmin = useAppSelector(selectUserIsAdmin);

  const flags = useFlag();

  const { t } = useTranslation();
  useUnauthorizedRedirect();

  const areDevCheatsEnabled =
    envConstants.DEV_CHEATS_UAT === "true" ||
    envConstants.DEV_CHEATS_STAGING === "true";

  const showDevSection =
    dev_ptlSimulation ||
    dev_conveyanceSimulation ||
    dev_orderCreation ||
    areDevCheatsEnabled;

  const OrdersComponent = flags.isNewOrdersPageEnabled ? OrdersNew : Orders;

  // contains routes that use the <BasicLayout> component for rendering the shell and navbar
  const basicLayoutRoutes = [
    {
      path: "/",
      component: (
        <RedirectToAutostoreMain>
          <ManualOps viewTitle="nav.viewname.manual ops" />
        </RedirectToAutostoreMain>
      )
    },
    {
      path: "/admin-batches",
      component: <AdminBatches viewTitle="nav.viewname.batches" />
    },
    {
      path: "/batches/:batchName/cart-prep",
      component: <BatchCartPrep viewTitle="nav.viewname.batches - cart prep" />
    },
    {
      path: "/batches/:batchId/pick",
      component: <Batch viewTitle="nav.viewname.batches - pick tasks" />
    },
    {
      path: "/batches/:batchName/stage",
      component: <StageTotes viewTitle="nav.viewname.batches - staging areas" />
    },
    {
      path: "/carts",
      component: <Carts viewTitle="nav.viewname.carts" />
    },
    {
      path: "/create-batches",
      component: <CreateBatches viewTitle="nav.viewname.create batches" />
    },
    {
      path: "/cycle-count/:cycleCountId",
      component: <CycleCount viewTitle="nav.viewname.manual cycle counts" />
    },
    {
      path: "/cycle-count",
      component: <CycleCounts viewTitle="nav.viewname.manual cycle counts" />
    },
    {
      path: "/demo",
      component: <DemoPage viewTitle="nav.viewname.autostore home" />
    },
    {
      path: "/analytics",
      component: <Analytics viewTitle="nav.viewname.analytics" />
    },
    {
      path: "/orders",
      component: <OrdersComponent viewTitle="nav.viewname.orders" />
    },
    {
      path: "/orders-new",
      component: <OrdersNew viewTitle="nav.viewname.orders" />
    },
    {
      path: "/pick-batches",
      component: <PickBatches viewTitle="nav.viewname.manual picking" />
    },
    {
      path: "/putaway",
      component: <Putaway viewTitle="nav.viewname.manual putaway" />
    },
    {
      path: "/settings",
      component: <SettingsRootRoute />
    },
    {
      path: "/settings/general",
      component: <SettingsGeneralSectionRoute />
    },
    {
      path: "/settings/workstation",
      component: <SettingsWorkstationSectionRoute />
    },
    {
      path: "/settings/bins",
      component: <SettingsBinsSectionRoute />
    },
    {
      path: "/settings/batches",
      component: <SettingsBatchSectionRoute />
    },
    {
      path: "/settings/grid",
      component: <SettingsGridSectionRoute />
    },
    {
      path: "/settings/api-tokens",
      component: <SettingsApiTokensSectionRoute />
    },
    {
      path: SETTINGS_PREVIEW_GENERAL_PATH,
      component: (
        <GeneralSettingsPage viewTitle="nav.viewname.settings general" />
      )
    },
    {
      path: SETTINGS_PREVIEW_WORKSTATION_PATH,
      component: (
        <SettingsWorkstationPage viewTitle="nav.viewname.settings workstation preview" />
      )
    },
    {
      path: SETTINGS_PREVIEW_BINS_PATH,
      component: <BinsSettingsPage viewTitle="nav.viewname.settings bins" />
    },
    {
      path: SETTINGS_PREVIEW_BATCHES_PATH,
      component: (
        <BatchesSettingsPage viewTitle="nav.viewname.settings batches" />
      )
    },
    {
      path: "/ship/consolidation",
      component: <Ship viewTitle="nav.viewname.order consolidation" />
    },
    {
      path: "/ship/order/:orderId",
      component: <ShipOrderTable viewTitle="nav.viewname.order" />
    },
    // Admin-only: each *SectionRoute wraps content in ConditionalFeatureRouteWrapper (see serviceSupport.routes).
    {
      path: "/service-support/cube-analytics",
      component: <ServiceSupportUnifySectionRoute />
    },
    {
      path: "/service-support/unify",
      component: <ServiceSupportUnifyLegacyPathRoute />
    },
    {
      path: "/service-support/status",
      component: <ServiceSupportStatusSectionRoute />
    },
    {
      path: "/service-support/tasks",
      component: <ServiceSupportTasksSectionRoute />
    },
    {
      path: "/service-support/events",
      component: <ServiceSupportEventsSectionRoute />
    },
    {
      path: "/service-support/andon",
      component: <ServiceSupportAndonSectionRoute />
    },
    {
      path: "/service-support/configurations",
      component: <ServiceSupportConfigurationsSectionRoute />
    },
    {
      path: "/service-support/testing-tools",
      component: <ServiceSupportTestingToolsSectionRoute />
    },
    {
      path: "/service-support",
      component: <ServiceSupportRootRoute />
    },
    {
      path: "/admin-summaries-workstation-view",
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={!!isAdmin}
          altMessage={t("user must be an administrator")}
        >
          <WorkstationDrillDownView viewTitle="nav.viewname.admin summaries workstation state view" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/dev-tools",
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={showDevSection}
          altMessage={t("restricted route")}
        >
          <DevToolsPage viewTitle="nav.viewname.dev tools" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: DEV_TOOLS_SERVICE_SUPPORT_EVENTS_PREVIEW_PATH,
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={showDevSection}
          altMessage={t("restricted route")}
        >
          <EventTablePreview />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: DEV_TOOLS_LOG_PUBLISHER_GRID_PATH,
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={showDevSection}
          altMessage={t("restricted route")}
        >
          <GridViewPage />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: DEV_TOOLS_ICON_LIST_PATH,
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={showDevSection}
          altMessage={t("restricted route")}
        >
          <IconTablePage />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: SETTINGS_PREVIEW_UPDATE_CONFIGURATION_PATH,
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={showDevSection}
          altMessage={t("restricted route")}
        >
          <UpdateConfigurationPage viewTitle="nav.viewname.settings update configuration" />
        </ConditionalFeatureRouteWrapper>
      )
    }
  ];

  const autostoreLayoutRoutes = [
    {
      path: "/autostore-main",
      component: <AutostoreMain viewTitle="nav.viewname.autostore home" />
    },
    {
      path: "/autostore-pick",
      component: <AutostorePicking viewTitle="nav.viewname.pick" />
    },
    {
      path: "/autostore-putaway",
      component: <AutostorePutawayOld viewTitle="nav.viewname.induction" />
    },
    {
      path: "/inventory-holds",
      component: (
        <InventoryHolds viewTitle="nav.viewname.manual inventory holds" />
      )
    },
    {
      path: "/picking-previous-bin",
      component: <PickingPreviousBin />
    },
    {
      path: "/putaway/:variantId",
      component: <PutawayTask viewTitle="nav.viewname.manual putaway" />
    },
    {
      path: "/start-bin-reconfiguration",
      component: (
        <StartBinReconfiguration viewTitle="nav.viewname.start bin reconfiguration" />
      )
    },
    {
      path: "/bin-reconfiguration",
      component: (
        <BinReconfiguration viewTitle="nav.viewname.bin reconfiguration" />
      )
    },
    {
      path: "/autostore-cyclecountv2",
      component: <CycleCountsV2 />
    },
    {
      path: "/autostore-cyclecountv2/cycle-count-task",
      component: <CycleCountTask />
    },
    // 3 port maintenance
    {
      path: "/workstation-bin-maintenance",
      component: (
        <BinMaintenanceWorkstation viewTitle="nav.viewname.bin maintenance" />
      )
    },
    {
      path: "/escalated-bins",
      component: <EscalatedBins viewTitle="nav.viewname.escalated bins" />
    },

    // inventory routes
    {
      path: "/autostore-inventory/holds",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory holds" />
    },
    {
      path: "/autostore-inventory/holds/product/:variantId",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory holds" />
    },
    {
      path: "/autostore-inventory/holds/bin/:binNumber",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory holds" />
    },
    {
      path: "/autostore-inventory",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory" />
    },
    {
      path: "/autostore-inventory/product",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory" />
    },
    {
      path: "/autostore-inventory/product/:variantId",
      component: (
        <AutostoreInventory viewTitle="nav.viewname.inventory - product" />
      )
    },
    {
      path: "/autostore-inventory/bin",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory" />
    },
    {
      path: "/autostore-inventory/bin/:binNumber",
      component: <AutostoreInventory viewTitle="nav.viewname.inventory - bin" />
    },
    {
      path: "/autostore-inventory/manage-flagged",
      component: (
        <ManageFlaggedInventory viewTitle="nav.viewname.manage flagged bins" />
      )
    },

    // conditional routes
    {
      path: "/bin-maintenance",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={binCleaningEnabled}>
          <CleaningBin viewTitle="nav.viewname.bin maintenance" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/view-bin-maintenance",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={binCleaningEnabled}>
          <CleaningBinView viewTitle="nav.viewname.bin maintenance" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/autostore-cyclecount",
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={isHomePageTypeIncluded(
            fc_autostore_homepages,
            "cycle counts"
          )}
        >
          <AutostoreCycleCounts viewTitle="nav.viewname.cycle counts" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/autostore-putaway-v2",
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={isHomePageTypeIncluded(
            fc_autostore_homepages,
            "induction v2"
          )}
        >
          <AutostorePutawayV2 viewTitle="nav.viewname.induction" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/order-creation",
      component: (
        <ConditionalFeatureRouteWrapper
          isAllowed={
            envConstants.DEV_CHEATS_UAT === "true" || dev_orderCreation
          }
        >
          <OrderCreation viewTitle="nav.viewname.order creation" />
        </ConditionalFeatureRouteWrapper>
      )
    },

    // manual routes
    {
      path: "/manual-inventory",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={!!manualOpsEnabled}>
          <ManualInventory viewTitle="nav.viewname.manual inventory" />
        </ConditionalFeatureRouteWrapper>
      )
    },

    {
      path: "/manual-inventory/product",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={!!manualOpsEnabled}>
          <ManualInventory viewTitle="nav.viewname.manual inventory" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/manual-inventory/product/:variantId",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={!!manualOpsEnabled}>
          <ManualInventory viewTitle="nav.viewname.manual inventory - product" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/manual-inventory/bin",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={!!manualOpsEnabled}>
          <ManualInventory viewTitle="nav.viewname.manual inventory" />
        </ConditionalFeatureRouteWrapper>
      )
    },
    {
      path: "/manual-inventory/bin/:binId",
      component: (
        <ConditionalFeatureRouteWrapper isAllowed={!!manualOpsEnabled}>
          <ManualInventory viewTitle="nav.viewname.manual inventory - bin" />
        </ConditionalFeatureRouteWrapper>
      )
    }
  ];

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/login/:name" element={<OrganizationLogin />} />

        <Route element={<LoginLayout />}>
          <Route
            path={`/login/${customClientBName}`}
            element={<CustomClientBLogin />}
          />
          <Route path="/callback" element={<AuthCallback />} />
          <Route path="/logout" element={<LogoutFlowSelector />} />
        </Route>

        {/* Private routes */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppProviders />}>
            {/* routes that use the AutostoreLayout to render view and navbar */}
            <Route element={<AutostoreLayout />}>
              {autostoreLayoutRoutes.map((info) => (
                <Route
                  key={info.path}
                  path={info.path}
                  element={
                    <ContextReset key={info.path}>
                      {info.component}
                    </ContextReset>
                  }
                />
              ))}
            </Route>
            {/* routes that use the BasicLayout to render view and navbar */}
            <Route element={<BasicLayout />}>
              {basicLayoutRoutes.map((info) => (
                <Route
                  key={info.path}
                  path={info.path}
                  element={
                    <ContextReset key={info.path}>
                      {info.component}
                    </ContextReset>
                  }
                />
              ))}
            </Route>
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </ErrorBoundary>
  );
};

export const router = createBrowserRouter([{ path: "*", element: <Root /> }], {
  future: {
    // affects relative links inside of wild card paths (ie. path/*) we have none of these.
    v7_relativeSplatPath: true,
    // affects only people using useFetcher
    v7_fetcherPersist: true,
    // affects people using useFetcher().formMethod and use navigation.formMethod
    v7_normalizeFormMethod: true,
    // affects only SSR frameworks.
    v7_partialHydration: true,
    // affects projects that user loaders
    v7_skipActionErrorRevalidation: true
  }
});
